import { Component, ElementRef, OnInit, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import {
  BarcodePrintStatusEnum,
  JoDetail,
  JoHeader,
  JoPullBatch,
} from "app/model/entities/entity-model";
import { JoStatusCode } from "app/model/enums/jo-status-code";
import { BarcodeService } from "app/shared/barcode.service";
import { DialogBaseComponent } from "app/shared/dialog-base.component";
import { DomainBaseComponent, wrapBusy } from "app/shared/domain-base.component";
import { DomainService } from "app/shared/domain.service";
import { JoUtilsService } from "app/shared/jo-utils.service";
import { PoUtilsService } from "app/shared/po-utils.service";
import { UtilFns } from "app/shared/util-fns";
import _ from "lodash";
import { takeUntil } from "rxjs/operators";

import { ItemDetailMatchSummary } from "@/model/entities/-item-detail-match-summary";
import { UnapplyJodDialogComponent } from "../job-orders/unapply-jod-dialog.component";

interface JoGroup {
  joHeader: JoHeader;
  joDetails: JoDetail[];
  collapsed: boolean;
}

interface ProductGroup {
  key: string;
  manufacturerName: string;
  description: string;
  style: string;
  featureChoicesExtract: string;
  productTypeId: number | undefined;
  joDetails: JoDetail[];
  // Summed across joDetails:
  orderQty: number;
  cancelQty: number;
  returnQty: number;
  appliedQty: number;
  poRemainingQty: number;
  openQty: number;
  inInventoryProgram: boolean;
  needsBarcode: boolean;
  addonCount: number;
}

export type PullViewMode = "product" | "jo";

@Component({
  selector: "app-notepad-pull",
  templateUrl: "./notepad-pull.component.html",
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class NotepadPullComponent extends DomainBaseComponent implements OnInit {
  joPullBatchId!: number;
  joPullBatch: JoPullBatch | undefined;

  joDetails: JoDetail[] = [];
  joHeaders: JoHeader[] = [];
  joGroups: JoGroup[] = [];

  allMatchSummary: ItemDetailMatchSummary[] = [];
  exactMatchSummary: ItemDetailMatchSummary[] = [];

  inventoryProductTypeMap = new Map<string, Set<number>>();

  canMarkComplete = false;
  canUncomplete = false;
  batchStatusLabel = "";
  shouldPrintBarcodes = true;
  searchText = "";

  viewMode: PullViewMode = "product";
  private static readonly VIEW_MODE_STORAGE_KEY = "np-pull-view-mode";

  productGroups: ProductGroup[] = [];

  expandedAddonJodIds = new Set<number>();
  expandedPullProductKeys = new Set<string>();

  constructor(
    protected domainService: DomainService,
    protected route: ActivatedRoute,
    private joUtilsService: JoUtilsService,
    private poUtilsService: PoUtilsService,
    private barcodeService: BarcodeService,
    private hostEl: ElementRef<HTMLElement>
  ) {
    super(domainService);
  }

  override async ngOnInit() {
    super.ngOnInit();
    this.joPullBatchId = +this.route.snapshot.params["joPullBatchId"];
    const sp = this.route.snapshot.queryParams["shouldPrintBarcodes"];
    this.shouldPrintBarcodes = sp !== "false";

    const savedMode = localStorage.getItem(NotepadPullComponent.VIEW_MODE_STORAGE_KEY);
    if (savedMode === "jo" || savedMode === "product") {
      this.viewMode = savedMode;
    }

    // Async match-summary loads (don't block first paint)
    this.dbQueryService.getItemDetailAllMatchSummaryForJoPullBatch(this.joPullBatchId).then((r) => {
      this.allMatchSummary = r;
    });
    this.dbQueryService.getItemDetailExactMatchSummaryForJoPullBatch(this.joPullBatchId).then((r) => {
      this.exactMatchSummary = r;
    });

    await this.loadBatch();
    this.isPageReady = true;

    // React to ?from=<jodId> changes (set by the apply screen on goBack()).
    // The route is /notepad/pull/:id, so Angular reuses this component instance
    // when query params change — ngOnInit does NOT fire again.
    this.route.queryParamMap.pipe(takeUntil(this.onDestroy)).subscribe(async (qpm) => {
      const fromParam = qpm.get("from");
      if (!fromParam) return;
      const fromJodId = +fromParam;
      // Reload entities AND match summaries — the apply screen may have
      // reserved an item, which changes both the JoDetails' qty calcs and
      // which items are still available for matching.
      await Promise.all([
        this.loadBatch(),
        this.dbQueryService.getItemDetailAllMatchSummaryForJoPullBatch(this.joPullBatchId).then((r) => {
          this.allMatchSummary = r;
        }),
        this.dbQueryService.getItemDetailExactMatchSummaryForJoPullBatch(this.joPullBatchId).then((r) => {
          this.exactMatchSummary = r;
        }),
      ]);
      await UtilFns.wait(50);
      this.scrollToNextAfter(fromJodId);
    });
  }

  private scrollToNextAfter(fromJodId: number) {
    if (this.viewMode === "jo") {
      const next = this.findNextPullableJod(fromJodId);
      if (next) this.scrollCardIntoView(`[data-jod-id="${next.id}"]`);
      return;
    }

    // product view
    const next = this.findNextPullableProductGroup(fromJodId);
    if (next) {
      this.expandedPullProductKeys.add(next.key);
      // wait for *ngIf to render the expanded picker
      void UtilFns.wait(1).then(() => this.scrollCardIntoView(`[data-product-key="${this.cssEscape(next.key)}"]`));
    }
  }

  private cssEscape(s: string): string {
    return s.replace(/["\\]/g, "\\$&");
  }

  private scrollCardIntoView(selector: string) {
    const el = this.hostEl.nativeElement.querySelector<HTMLElement>(selector);
    if (!el) return;

    const scroller = this.findScrollParent(el);
    if (!scroller) return;

    const targetTop = el.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
    scroller.scrollTo({ top: targetTop, behavior: "smooth" });
  }

  private findScrollParent(el: HTMLElement): HTMLElement | null {
    let cur: HTMLElement | null = el.parentElement;
    while (cur) {
      const style = getComputedStyle(cur);
      const oy = style.overflowY;
      if ((oy === "auto" || oy === "scroll") && cur.scrollHeight > cur.clientHeight) {
        return cur;
      }
      cur = cur.parentElement;
    }
    return null;
  }

  private findNextPullableJod(fromJodId: number): JoDetail | undefined {
    const visible = this.filteredGroups.flatMap((g) => g.joDetails);
    if (visible.length === 0) return undefined;
    const fromIdx = visible.findIndex((j) => j.id === fromJodId);
    const ordered = fromIdx >= 0 ? [...visible.slice(fromIdx + 1), ...visible.slice(0, fromIdx + 1)] : visible;
    return ordered.find((j) => this.canDisplayPull(j));
  }

  private findNextPullableProductGroup(fromJodId: number): ProductGroup | undefined {
    const visible = this.filteredProductGroups;
    if (visible.length === 0) return undefined;
    const fromIdx = visible.findIndex((g) => g.joDetails.some((j) => j.id === fromJodId));
    const ordered = fromIdx >= 0 ? [...visible.slice(fromIdx + 1), ...visible.slice(0, fromIdx + 1)] : visible;
    return ordered.find((g) => this.canDisplayPullProduct(g));
  }

  private updateBatchState() {
    const total = this.joHeaders.length;
    const incompleteCount = this.joHeaders.filter((joh) => joh.joStatusId === JoStatusCode.OpenProcessIncomplete).length;

    this.canMarkComplete = incompleteCount > 0;
    this.canUncomplete = this.joHeaders.some((joh) => joh.joStatusId === JoStatusCode.OpenProcessComplete);

    if (total === 0) {
      this.batchStatusLabel = "No Job Orders";
    } else if (incompleteCount === 0) {
      this.batchStatusLabel = "Process Complete";
    } else if (incompleteCount === total) {
      this.batchStatusLabel = "Process Incomplete";
    } else {
      this.batchStatusLabel = `Partial (${incompleteCount} of ${total} open)`;
    }
  }

  private async loadBatch() {
    this.joDetails = await this.dbQueryService.getJoDetailsByJoPullBatchId(this.joPullBatchId);
    this.joHeaders = _.uniq(this.joDetails.map((jod) => jod.joHeader));
    this.updateBatchState();

    // Build inventory program → product-type-id set map
    const programNames = _.uniq(
      this.joHeaders
        .map((joh) => (joh.allowInventory ? joh.inventoryProgramName : null))
        .filter((n): n is string => !!n)
    );
    this.inventoryProductTypeMap = new Map();
    await Promise.all(
      programNames.map(async (name) => {
        const ids = await this.dbQueryService.getAccountOwnedProductTypeIds(name);
        this.inventoryProductTypeMap.set(name, new Set(ids));
      })
    );

    this.rebuildGroups();
    this.rebuildProductGroups();
  }

  setViewMode(mode: PullViewMode) {
    this.viewMode = mode;
    localStorage.setItem(NotepadPullComponent.VIEW_MODE_STORAGE_KEY, mode);
  }

  private rebuildProductGroups() {
    const keyFor = (jod: JoDetail) => {
      const mfg = jod.product?.productType?.manufacturer?.name ?? "";
      const ptId = jod.product?.productTypeId ?? 0;
      const fc = jod.product?.featureChoicesExtract ?? "";
      return `${mfg}|${ptId}|${fc}`;
    };

    const grouped = _.groupBy(this.joDetails, keyFor);

    const groups: ProductGroup[] = Object.entries(grouped).map(([key, jods]) => {
      const first = jods[0];
      const pt = first.product?.productType;
      const orderQty = _.sumBy(jods, (j) => j.orderQty);
      const cancelQty = _.sumBy(jods, (j) => j.cancelQty);
      const returnQty = _.sumBy(jods, (j) => j.returnQty);
      const appliedQty = _.sumBy(jods, (j) => j.getItemDetailsRcvdQty());
      const poRemainingQty = _.sumBy(jods, (j) => j.getPoDetailsRemainingQty());
      const openQty = _.sumBy(jods, (j) => j.getUnresolvedQty());
      const inInventoryProgram = jods.some((j) => this.isInInventoryProgram(j));
      const needsBarcode = jods.some((j) => this.needsBarcode(j));
      const addonCount = _.sumBy(jods, (j) => j.joDetailAddons?.length ?? 0);

      return {
        key,
        manufacturerName: pt?.manufacturer?.name ?? "",
        description: pt?.description ?? "",
        style: pt?.style ?? "",
        featureChoicesExtract: first.product?.featureChoicesExtract ?? "",
        productTypeId: pt?.id,
        joDetails: jods,
        orderQty,
        cancelQty,
        returnQty,
        appliedQty,
        poRemainingQty,
        openQty,
        inInventoryProgram,
        needsBarcode,
        addonCount,
      };
    });

    this.productGroups = _.orderBy(
      groups,
      [(g) => g.manufacturerName, (g) => g.description, (g) => g.style, (g) => g.featureChoicesExtract],
      ["asc", "asc", "asc", "asc"]
    );
  }

  private rebuildGroups() {
    const sortedJods = _.orderBy(
      this.joDetails,
      [
        (jod) => jod.product?.productType?.manufacturer?.name ?? "",
        (jod) => jod.product?.productType?.description ?? "",
        (jod) => jod.product?.productType?.style ?? "",
      ],
      ["asc", "asc", "asc"]
    );
    const grouped = _.groupBy(sortedJods, (jod) => jod.joHeaderId);
    const previousCollapsed = new Map(this.joGroups.map((g) => [g.joHeader.id, g.collapsed] as const));

    this.joGroups = this.joHeaders.map((joh) => ({
      joHeader: joh,
      joDetails: grouped[joh.id] ?? [],
      collapsed: previousCollapsed.get(joh.id) ?? false,
    }));
  }

  goBack() {
    this.router.navigate(["/notepad/pull-batches"]);
  }

  toggleGroup(group: JoGroup) {
    group.collapsed = !group.collapsed;
  }

  updateLocation() {
    const urlTree = this.router.createUrlTree([`/notepad/pull/${this.joPullBatchId}`], {
      queryParams: { shouldPrintBarcodes: this.shouldPrintBarcodes },
    });
    this.domainService.location.replaceState(urlTree.toString());
  }

  // -------- search/filter --------

  get filteredGroups(): JoGroup[] {
    const q = this.searchText.trim().toLowerCase();
    if (!q) return this.joGroups;

    return this.joGroups
      .map((g) => {
        const filtered = g.joDetails.filter((jod) => this.jodMatches(jod, q));
        return { ...g, joDetails: filtered };
      })
      .filter((g) => g.joDetails.length > 0);
  }

  private jodMatches(jod: JoDetail, q: string): boolean {
    const haystack = [
      jod.product?.productType?.manufacturer?.name,
      jod.product?.productType?.description,
      jod.product?.productType?.style,
      jod.product?.featureChoicesExtract,
      String(jod.joHeaderId ?? ""),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  }

  // -------- per-line helpers --------

  isInInventoryProgram(jod: JoDetail): boolean {
    const progName = jod.joHeader?.inventoryProgramName;
    if (!progName || !jod.joHeader.allowInventory) return false;
    return this.inventoryProductTypeMap.get(progName)?.has(jod.product?.productTypeId) ?? false;
  }

  canDisplayPull(jod: JoDetail): boolean {
    return jod.getUnresolvedQty() > 0 && !jod.isAccountOwned() && this.areAnyItdAvailable(jod);
  }

  canDisplayUndo(jod: JoDetail): boolean {
    return jod.getItemDetailsRcvdQty() > 0 && !jod.isAccountOwned();
  }

  areAnyItdAvailable(jod: JoDetail): boolean {
    if (this.allMatchSummary == null || this.exactMatchSummary == null) return true;
    return this.allMatchSummary.some((x) => x.joDetailId == jod.id);
  }

  pullLabel(jod: JoDetail): string {
    const label = "Pull";
    if (this.allMatchSummary == null || this.exactMatchSummary == null) return label;
    const allCount = this.allMatchSummary.find((x) => x.joDetailId == jod.id)?.matchingCount ?? 0;
    const exactCount = this.exactMatchSummary.find((x) => x.joDetailId == jod.id)?.matchingCount ?? 0;
    if (allCount > 0) {
      return `${label} (${exactCount}/${allCount - exactCount})`;
    }
    return label;
  }

  // -------- product-group view helpers --------

  canDisplayPullProduct(group: ProductGroup): boolean {
    return group.joDetails.some((jod) => this.canDisplayPull(jod));
  }

  pullLabelProduct(group: ProductGroup): string {
    const label = "Pull";
    if (this.allMatchSummary == null || this.exactMatchSummary == null) return label;
    const jodIds = new Set(group.joDetails.map((j) => j.id));
    const allCount = _.sumBy(
      this.allMatchSummary.filter((x) => jodIds.has(x.joDetailId)),
      (x) => x.matchingCount
    );
    const exactCount = _.sumBy(
      this.exactMatchSummary.filter((x) => jodIds.has(x.joDetailId)),
      (x) => x.matchingCount
    );
    if (allCount > 0) {
      return `${label} (${exactCount}/${allCount - exactCount})`;
    }
    return label;
  }

  isPullProductExpanded(group: ProductGroup): boolean {
    return this.expandedPullProductKeys.has(group.key);
  }

  togglePullProduct(group: ProductGroup, event?: Event) {
    event?.stopPropagation();
    // Shortcut: if there's only one pullable JoDetail, skip the picker
    // and jump straight to the scanner.
    const pullable = this.productGroupPullableJoDetails(group);
    if (pullable.length === 1) {
      this.onPull(pullable[0]);
      return;
    }
    if (this.expandedPullProductKeys.has(group.key)) {
      this.expandedPullProductKeys.delete(group.key);
    } else {
      this.expandedPullProductKeys.add(group.key);
    }
  }

  productGroupPullableJoDetails(group: ProductGroup): JoDetail[] {
    return group.joDetails.filter((jod) => this.canDisplayPull(jod));
  }

  get filteredProductGroups(): ProductGroup[] {
    const q = this.searchText.trim().toLowerCase();
    if (!q) return this.productGroups;
    return this.productGroups.filter((g) => {
      const haystack = [g.manufacturerName, g.description, g.style, g.featureChoicesExtract]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }

  needsBarcode(jod: JoDetail): boolean {
    return jod.itemDetails.some((itd) => itd.barcodePrintStatusId == BarcodePrintStatusEnum.NotYetPrinted);
  }

  addonCount(jod: JoDetail): number {
    return jod.joDetailAddons?.length ?? 0;
  }

  isAddonExpanded(jod: JoDetail): boolean {
    return this.expandedAddonJodIds.has(jod.id);
  }

  toggleAddons(jod: JoDetail, event?: Event) {
    event?.stopPropagation();
    if (this.expandedAddonJodIds.has(jod.id)) {
      this.expandedAddonJodIds.delete(jod.id);
    } else {
      this.expandedAddonJodIds.add(jod.id);
    }
  }

  // -------- actions --------

  onPull(jod: JoDetail) {
    this.router.navigate([
      "/notepad/pull",
      this.joPullBatchId,
      "apply",
      jod.id,
      this.shouldPrintBarcodes,
    ]);
  }

  async onUndo(jod: JoDetail) {
    await DialogBaseComponent.open(UnapplyJodDialogComponent, this, { joDetail: jod });
    this.rebuildGroups();
    this.rebuildProductGroups();
  }

  @wrapBusy
  async onDeleteJoFromBatch(joh: JoHeader) {
    const ok = await this.dialogService.yesNoDialog(
      "Remove Job Order",
      `Remove JO ${joh.id} from this batch?`
    );
    if (!ok) return;
    this.toastr.info("Removing Job Order from pull batch");
    await this.joUtilsService.deleteJoHeaderFromBatch(joh.id);
    this.toastr.success("Job Order removed");
    await this.loadBatch();
  }

  @wrapBusy
  async onMarkBatchComplete() {
    const ok = await this.dialogService.yesNoDialog(
      "Mark Batch Complete",
      "All unpulled items will be submitted for order. Continue?"
    );
    if (!ok) return;

    this.toastr.info("Marking batch complete and creating order details");
    // Only advance job orders still Open/Process Incomplete; Closed ones are left alone,
    // and their JoDetails are excluded so no PO is cut against a closed order.
    const joHeadersToAdvance = this.joHeaders.filter((joh) => joh.joStatusId === JoStatusCode.OpenProcessIncomplete);
    const advanceIdSet = new Set(joHeadersToAdvance.map((joh) => joh.id));
    const joDetailsToOrder = this.joDetails.filter((jod) => advanceIdSet.has(jod.joHeaderId));

    const odCount = await this.joUtilsService.createPurchaseOrdersForJoDetails(joDetailsToOrder);
    // Set the status after the PO lines exist, so no save can commit a complete job order without them.
    joHeadersToAdvance.forEach((joh) => (joh.joStatusId = JoStatusCode.OpenProcessComplete));
    await this.dbSaveService.saveChanges();
    this.updateBatchState();
    this.toastr.success(`Created ${odCount} Purchase Order Detail record(s)`);
    this.goBack();
  }

  @wrapBusy
  async onUncompleteBatch() {
    const { ok, pohIdSet } = await this.joUtilsService.uncompleteBatch(this.joPullBatchId);
    if (ok) {
      this.poUtilsService.deleteStrandedPoHeaders(pohIdSet);
      this.updateBatchState();
    }
  }

  @wrapBusy
  async onPrintBarcodes() {
    const itemDetails = _.flatMap(this.joDetails, (jod) =>
      jod.itemDetails.filter((itd) => itd.barcodePrintStatusId == BarcodePrintStatusEnum.NotYetPrinted)
    );
    const changes = await this.barcodeService.printItemDetailBarcodesAndNewAddonBinLabels(itemDetails);
    if (changes.length > 0) {
      await this.dbSaveService.saveSelectedChanges(changes);
    }
  }

  onChangeBatch() {
    this.router.navigate(["/notepad/pull-batches"]);
  }

  canPrintItemDetailBarcodes(): boolean {
    return this.joDetails.some((jod) =>
      jod.itemDetails.some((itd) => itd.barcodePrintStatusId == BarcodePrintStatusEnum.NotYetPrinted)
    );
  }

  canDeleteJoHeaderFromBatch(joh: JoHeader): boolean {
    return joh != null && joh.joStatusId === JoStatusCode.OpenProcessIncomplete;
  }
}
